import React, { useEffect, useState } from "react";
import { collection, getDocs, getDoc, doc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { signOut } from 'firebase/auth';
import { Link, useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
// import "./FinesPage.css";

const CollectedFinesPage = () => {
  const [finesData, setFinesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("userName");
  const [sortDirection, setSortDirection] = useState("asc");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFines = async () => {
      setLoading(true);
      try {
        const finesCollection = collection(db, "Fines");
        const usersSnapshot = await getDocs(finesCollection);

        const data = [];

        for (const userDoc of usersSnapshot.docs) {
          const userId = userDoc.id;
          const userFinesCollection = collection(db, "Fines", userId, "fines");
          const finesSnapshot = await getDocs(userFinesCollection);

          const fines = finesSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          const totalFine = userDoc.data().TotalFine || 0;
          const pendingFine = userDoc.data().PendingFine || 0;
          const collectedFine = totalFine - pendingFine;

          const userInfoDoc = await getDoc(doc(db, "users", userId));
          const userName = userInfoDoc.exists()
            ? userInfoDoc.data().userName || "Unknown User"
            : "Unknown User";

          // Only add users with collected fines
          if (collectedFine > 0) {
            data.push({
              userId,
              userName,
              fines: fines.filter(fine => fine.status === "Paid"),
              totalFine,
              pendingFine,
              collectedFine
            });
          }
        }

        setFinesData(data);
      } catch (error) {
        console.error("Error fetching fines:", error);
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Failed to load collected fines data",
          confirmButtonColor: "#3085d6",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchFines();
  }, []);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error("Logout error:", error);
      Swal.fire({
        icon: "error",
        title: "Logout Failed",
        text: "Please try again",
      });
    }
  };

  const sortedData = [...finesData].sort((a, b) => {
    if (sortField === "totalFine" || sortField === "pendingFine" || sortField === "collectedFine") {
      return sortDirection === "asc"
        ? a[sortField] - b[sortField]
        : b[sortField] - a[sortField];
    } else {
      return sortDirection === "asc"
        ? a[sortField]?.localeCompare(b[sortField])
        : b[sortField]?.localeCompare(a[sortField]);
    }
  });

  const filteredData = sortedData.filter(
    (user) => {
      return user.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.userId.toLowerCase().includes(searchTerm.toLowerCase());
    }
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-PK", {
      style: "currency",
      currency: "PKR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const totalCollectedFines = finesData.reduce(
    (sum, user) => sum + user.collectedFine,
    0
  );
  
  const totalAllFines = finesData.reduce(
    (sum, user) => sum + (user.totalFine || 0),
    0
  );
  
  const totalPendingFines = finesData.reduce(
    (sum, user) => sum + (user.pendingFine || 0),
    0
  );
  
  const collectionRate = totalAllFines
    ? Math.round(((totalAllFines - totalPendingFines) / totalAllFines) * 100)
    : 0;

  return (
    <div className="collected-fines-container min-vh-100">
      {/* Top Navigation Bar */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-success shadow-sm sticky-top">
        <div className="container-fluid">
          <Link className="navbar-brand fw-bold d-flex align-items-center" to="/dashboard">
            <i className="bi bi-cash-coin me-2"></i>
            FineTracker
          </Link>
          
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <button className="btn btn-success" type="button" id="userMenu" data-bs-toggle="dropdown" aria-expanded="false">
                <i className="bi bi-person-circle fs-5"></i>
              </button>
              <ul className="dropdown-menu dropdown-menu-end shadow border-0" aria-labelledby="userMenu">
                <li><Link className="dropdown-item" to="/profile"><i className="bi bi-person me-2"></i> Profile</Link></li>
                <li><Link className="dropdown-item" to="/settings"><i className="bi bi-gear me-2"></i> Settings</Link></li>
                <li><hr className="dropdown-divider" /></li>
                <li><button className="dropdown-item text-danger" onClick={handleLogout}><i className="bi bi-box-arrow-right me-2"></i> Logout</button></li>
              </ul>
            </div>
          </div>
        </div>
      </nav>

      <div className="container-fluid py-4 px-3 px-md-4">
        {/* Page Header */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
          <div>
            <h1 className="h2 fw-bold mb-1">Collected Fines</h1>
            <p className="text-muted mb-0">View all successfully collected fines</p>
          </div>
          
          <div className="d-flex gap-2">
            <Link to="/dashboard" className="btn btn-outline-success">
              <i className="bi bi-speedometer2 me-2"></i> Back to Dashboard
            </Link>
            <Link to="/fines" className="btn btn-success">
              <i className="bi bi-plus-circle me-2"></i> Add New Fine
            </Link>
          </div>
        </div>

        {/* Main Statistics Card - Highlighted */}
        <div className="card border-0 shadow-sm mb-4 bg-success bg-opacity-10">
          <div className="card-body py-4">
            <div className="row align-items-center">
              <div className="col-12 col-md-6">
                <h2 className="display-4 fw-bold text-success mb-0">
                  {formatCurrency(totalCollectedFines)}
                </h2>
                <p className="lead mb-0">Total Collected Fines</p>
                <p className="text-muted mt-2">
                  <small>Calculated as Total Fine Amount - Pending Fine Amount</small>
                </p>
              </div>
              <div className="col-12 col-md-6">
                <div className="row g-3 mt-4 mt-md-0">
                  <div className="col-12 col-md-6">
                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        <div className="d-flex justify-content-between">
                          <div>
                            <p className="text-muted mb-1">Total Fines</p>
                            <h4 className="mb-0 fw-bold">{formatCurrency(totalAllFines)}</h4>
                          </div>
                          <div className="stats-icon-sm bg-primary bg-opacity-10 text-primary">
                            <i className="bi bi-cash-stack"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        <div className="d-flex justify-content-between">
                          <div>
                            <p className="text-muted mb-1">Collection Rate</p>
                            <h4 className="mb-0 fw-bold">{collectionRate}%</h4>
                          </div>
                          <div className="stats-icon-sm bg-success bg-opacity-10 text-success">
                            <i className="bi bi-percent"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white py-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center">
            <h5 className="fw-bold mb-3 mb-md-0">Collected Fines by User</h5>
            
            <div className="position-relative search-container">
              <i className="bi bi-search position-absolute search-icon"></i>
              <input
                type="text"
                className="form-control search-input"
                placeholder="Search users or ID..."
                value={searchTerm}
                onChange={handleSearch}
              />
              {searchTerm && (
                <button 
                  className="btn btn-sm position-absolute search-clear-btn"
                  onClick={() => setSearchTerm("")}
                >
                  <i className="bi bi-x"></i>
                </button>
              )}
            </div>
          </div>

          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-success" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading fines data...</p>
              </div>
            ) : filteredData.length === 0 ? (
              <div className="py-5 text-center">
                <div className="empty-state-icon">
                  <i className="bi bi-search"></i>
                </div>
                <h5 className="mt-3">No Results Found</h5>
                <p className="text-muted mb-3">
                  No users with collected fines match your search criteria
                </p>
                {searchTerm && (
                  <button
                    className="btn btn-outline-success"
                    onClick={() => setSearchTerm("")}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Clear Search
                  </button>
                )}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort("userName")} className="sortable-header">
                        User
                        <i className={`bi bi-arrow-${sortField === "userName" ? (sortDirection === "asc" ? "up" : "down") : "down-up"} ms-1`}></i>
                      </th>
                      <th onClick={() => handleSort("totalFine")} className="sortable-header">
                        Total Fine
                        <i className={`bi bi-arrow-${sortField === "totalFine" ? (sortDirection === "asc" ? "up" : "down") : "down-up"} ms-1`}></i>
                      </th>
                      <th onClick={() => handleSort("pendingFine")} className="sortable-header">
                        Pending
                        <i className={`bi bi-arrow-${sortField === "pendingFine" ? (sortDirection === "asc" ? "up" : "down") : "down-up"} ms-1`}></i>
                      </th>
                      <th onClick={() => handleSort("collectedFine")} className="sortable-header">
                        Collected
                        <i className={`bi bi-arrow-${sortField === "collectedFine" ? (sortDirection === "asc" ? "up" : "down") : "down-up"} ms-1`}></i>
                      </th>
                      <th>Paid Fines</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((user) => (
                      <tr key={user.userId}>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="avatar-circle bg-success text-white me-3">
                              {user.userName.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <h6 className="mb-0">{user.userName}</h6>
                              <small className="text-muted">{user.userId}</small>
                            </div>
                          </div>
                        </td>
                        <td className="fw-medium">{formatCurrency(user.totalFine)}</td>
                        <td className="fw-medium">{formatCurrency(user.pendingFine)}</td>
                        <td className="fw-medium text-success">{formatCurrency(user.collectedFine)}</td>
                        <td>{user.fines.length}</td>
                        <td>
                          <button 
                            className="btn btn-sm btn-outline-success me-2" 
                            data-bs-toggle="collapse" 
                            data-bs-target={`#collapse-${user.userId}`} 
                            aria-expanded="false"
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                          <Link to={`/fines/${user.userId}`} className="btn btn-sm btn-outline-primary">
                            <i className="bi bi-list-ul"></i>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Expanded User Details */}
        {filteredData.map((user) => (
          <div key={`collapse-${user.userId}`} className="collapse mb-4" id={`collapse-${user.userId}`}>
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0 fw-bold">{user.userName}'s Collected Fines</h6>
                  <button className="btn btn-sm btn-outline-secondary" data-bs-toggle="collapse" data-bs-target={`#collapse-${user.userId}`}>
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
              <div className="card-body p-0">
                {user.fines.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-striped mb-0">
                      <thead>
                        <tr>
                          <th>Reason</th>
                          <th>Amount</th>
                          <th>Date</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {user.fines.map((fine, index) => (
                          <tr key={index}>
                            <td>{fine.fineType}</td>
                            <td className="fw-medium">{formatCurrency(fine.amount)}</td>
                            <td>{fine.date}</td>
                            <td>
                              <span className="badge bg-success rounded-pill px-3 py-2">
                                <i className="bi bi-check-circle me-1"></i> Paid
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="bi bi-inbox display-4 text-muted"></i>
                    <p className="mt-3 mb-0">No collected fines recorded for this user</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <footer className="mt-auto py-3 bg-light">
        <div className="container-fluid">
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center">
            <p className="small text-muted mb-2 mb-sm-0">© 2025 FineTracker. All rights reserved.</p>
            <div>
              <button className="btn btn-sm btn-link text-muted">Help</button>
              <button className="btn btn-sm btn-link text-muted">Privacy</button>
              <button className="btn btn-sm btn-link text-muted">Terms</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CollectedFinesPage;